import { useState } from 'react';
import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import {
  getRoundedPrecision,
  intlAssetValueFormatting,
  PrecisionFloat,
  type TimeScalar,
} from '@bts-web/utils-formatting';
import { convertToLocaleDateString } from '@bts-web/utils-formatting-client';
import {
  CryptoTransfersVASPQuery,
  UpdateExternalCryptoAddressInput,
} from '@bts-web/data-layer/server';
import { css } from '@bts-web/utils-style-engine';
import { TFunction } from 'i18next';
import { getCryptoTransferTranslations } from '../../../utils/getCryptoTransferTranslations';
import { useHandleGqlErrorsWithSnackbar } from '../../../../common/utils/errors';
import {
  updateExternalCryptoAddress,
  markNotificationAsRead,
} from '../../../../common/gqlActions';
import { WalletForm } from '../../Reusable/WalletForm/WalletForm';
import {
  CryptoTransfersTransactionsAssetType,
  CryptoTransfersTransactionsExternalAddressType,
} from '../../../../transaction-details/types';
import { useAppNotification } from '../../../../notifications/NotificationContextProvider';
import {
  TransferAssetDataType,
  TransferNetworkDataType,
} from '../../../utils/useTransfersNextStepState.client';
import { WalletOwnerType } from '../../../types';
import { WalletOwnerTypeScreen } from '../../SendScreen/WalletOwnerTypeScreen/WalletOwnerTypeScreen';

export const transactionDetailsDrawerId = 'transaction_details_drawer';

const labelStyles = css({
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
  color: 'neutrals.text_secondary',
});

const captionStyles = css({
  fontSize: 'caption.medium_medium',
  fontWeight: 'caption.medium_medium',
  lineHeight: 'caption.medium_medium',
  letterSpacing: 'caption.medium_medium',
  color: 'neutrals.text_primary',
});

const containerStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  py: 'small',
  borderBottomWidth: '1px',
  borderBottomColor: 'neutrals.divider',
});

export const SelfDeclarationScreen = ({
  assetData,
  networkData,
  externalAddress,
  createdAt,
  walletHosts,
  assetAmount,
  finishWalletUpdate,
  notificationId,
}: {
  assetData: CryptoTransfersTransactionsAssetType | null;
  networkData: TransferNetworkDataType | null;
  externalAddress: CryptoTransfersTransactionsExternalAddressType;
  walletHosts: CryptoTransfersVASPQuery['response']['cryptoTransfersVASP'];
  createdAt: TimeScalar | null;
  assetAmount: PrecisionFloat;
  notificationId?: string;
  onClose?: () => void;
  finishWalletUpdate: () => void;
}) => {
  const [walletOwnerType, setWalletOwnerType] = useState<WalletOwnerType>(
    (externalAddress?.ownerType as WalletOwnerType) ?? WalletOwnerType.None,
  );

  const { t } = useClientTranslation();

  const translations = getCryptoTransferTranslations(t as TFunction);

  const { setAppNotification } = useAppNotification();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const locale = useClientLocale();

  const createdAtDate = convertToLocaleDateString(createdAt, locale);

  const assetAmountFormatted = `+ ${intlAssetValueFormatting(
    Number(getRoundedPrecision(assetAmount)),
    {
      locale,
      fractionDigits: assetAmount?.precision ?? 8,
    },
  )} ${assetData?.symbol}`;

  const isLegalEntity = walletOwnerType === WalletOwnerType.LegalEntity;

  const handleUpdate = async (
    walletFormData: Partial<UpdateExternalCryptoAddressInput>,
  ) => {
    try {
      const response = await updateExternalCryptoAddress({
        ...walletFormData,
        id: externalAddress?.id as string,
      } as UpdateExternalCryptoAddressInput);

      if (response?.errors) {
        processErrors(response?.errors);

        return;
      }

      if (notificationId) {
        await markNotificationAsRead({
          id: notificationId,
        });

        if (response?.errors) {
          processErrors(response?.errors);

          return;
        }
      }

      setAppNotification({
        title: translations.cryptoDepositProcessingInformationTitle,
        subtitle: translations.cryptoDepositProcessingConfirmationMessage,
        visual: 'info',
        customDuration: 3000,
        position: 'bottomRight',
      });
    } catch (_error) {
      processErrors();
    } finally {
      finishWalletUpdate();
    }
  };

  if (walletOwnerType === WalletOwnerType.None) {
    return (
      <div
        className={css({
          px: 'medium',
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <WalletOwnerTypeScreen
          onSelect={setWalletOwnerType}
          labels={{
            title: translations.cryptoDepositWalletOwnerTypeQuestion,
            subtitle: translations.cryptoDepositWalletOwnerTypeSubtitle,
            learnMore: translations.learnMore,
            personTitle: translations.walletOwnerTypePersonTitle,
            personSubtitle: translations.walletOwnerTypePersonSubtitle,
            businessTitle: translations.walletOwnerTypeBusinessTitle,
            businessSubtitle:
              translations.cryptoDepositWalletOwnerTypeBusinessSubtitle,
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={css({
        px: 'medium',
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
      })}
    >
      <WalletForm
        recipientNameLabel={
          isLegalEntity
            ? translations.registeredBusinessName
            : translations.cryptoDepositSendersName
        }
        actionLabel={translations.receive}
        title={translations.cryptoDepositSendersInformation}
        translations={translations}
        assetData={
          {
            name: assetData?.name as string,
            logoUrl: assetData?.logoUrl as string,
            symbol: assetData?.symbol as string,
          } as TransferAssetDataType
        }
        networkData={networkData as TransferNetworkDataType}
        defaultAddressInput={{
          address: externalAddress?.address,
        }}
        walletHosts={walletHosts}
        onUpdate={handleUpdate}
        submitButtonLabel={translations.confirm}
        isWalletAddressDisabled
        isLegalEntity={isLegalEntity}
      >
        <div className={css({ pb: 'small' })}>
          {assetAmount && (
            <div className={containerStyles}>
              <div className={labelStyles}>
                {translations.cryptoDepositAmount}
              </div>
              <div className={captionStyles}>{assetAmountFormatted}</div>
            </div>
          )}

          {createdAt && (
            <div className={containerStyles}>
              <div className={labelStyles}>{translations.date}</div>
              <div className={captionStyles}>{createdAtDate}</div>
            </div>
          )}
        </div>
      </WalletForm>
    </div>
  );
};
